import { subDays } from "date-fns";

import type {
  AmpProjectLinkFragment,
  AmpProjectLinkSourceFragment,
} from "src/graphql";
import {
  compareLinkKinds,
  type DescribedLink,
  describeLink,
  LINK_KIND_LABELS,
  type LinkKind,
} from "src/pages/projects/links";

/** A link on a task or subtask names it, when it shows on a parent's page. */
export type AttachmentLink = AmpProjectLinkFragment &
  Partial<AmpProjectLinkSourceFragment>;

export const NO_ATTACHMENTS_MESSAGE =
  "Attach ads, emails, web pages, files, links, audiences, or syncs. Briefs and other linked work show up here too.";

/** What an attachment is, then the facts about it, for a line such as "Audience · 48,200 members". */
export function attachmentSummary(
  described: DescribedLink,
  facts: string[],
): string[] {
  return [
    LINK_KIND_LABELS[described.kind],
    // An email's facts lead with its subject, which is also its subtitle.
    ...(described.subtitle && described.kind !== "email"
      ? [described.subtitle]
      : []),
    ...facts,
  ];
}

/** Where an attachment hangs: the project itself, or one of its tasks. */
export const ON_PROJECT = "project";

// Work a project produces, which the Attachments tab shows as previews rather than rows.
const CREATIVE_KINDS: ReadonlySet<LinkKind> = new Set([
  "ad",
  "email",
  "sms",
  "push",
  "document",
  "webPage",
  "canvas",
]);

export type ProjectAttachment = {
  link: AttachmentLink;
  described: DescribedLink;
  /** The Library file to preview, for creative work that is one. */
  fileId: string | null;
  /** The task it's attached through, or `ON_PROJECT`. */
  attachedTo: string;
  /** When it was attached or its file last changed, whichever is later. */
  touchedAt: string;
};

export function toProjectAttachments(
  links: AttachmentLink[],
): ProjectAttachment[] {
  return links.map((link) => {
    const described = describeLink(link);
    const fileUpdatedAt = link.amp_file?.updated_at;
    return {
      link,
      described,
      fileId:
        CREATIVE_KINDS.has(described.kind) && link.amp_file
          ? link.amp_file.id
          : null,
      attachedTo: link.source_task?.id ?? ON_PROJECT,
      touchedAt:
        fileUpdatedAt && toTime(fileUpdatedAt) > toTime(link.created_at)
          ? fileUpdatedAt
          : link.created_at,
    };
  });
}

/** The Library file behind an uploaded attachment, which previews only when it's an image. */
export function uploadFileId({ link }: ProjectAttachment): string | null {
  return link.amp_file?.type === "upload" ? link.amp_file.id : null;
}

/** Newest first, by when each was attached or its file last changed, as the Attachments tab lists them. */
export function newestFirst(a: ProjectAttachment, b: ProjectAttachment) {
  return toTime(b.touchedAt) - toTime(a.touchedAt);
}

export type AttachmentFilters = {
  search: string;
  /** Null shows every kind. */
  kinds: ReadonlySet<LinkKind> | null;
  /** Null shows attachments on the project and every task. */
  attachedTo: ReadonlySet<string> | null;
};

export function filterAttachments(
  attachments: ProjectAttachment[],
  { search, kinds, attachedTo }: AttachmentFilters,
): ProjectAttachment[] {
  const query = search.trim().toLowerCase();
  return attachments.filter(
    ({ described, attachedTo: target }) =>
      (kinds === null || kinds.has(described.kind)) &&
      (attachedTo === null || attachedTo.has(target)) &&
      (query === "" || described.title.toLowerCase().includes(query)),
  );
}

/** Creative work with a file to preview, and everything else. */
export function splitCreative(attachments: ProjectAttachment[]): {
  creative: ProjectAttachment[];
  other: ProjectAttachment[];
} {
  return {
    creative: attachments.filter(({ fileId }) => fileId !== null),
    other: attachments.filter(({ fileId }) => fileId === null),
  };
}

export const ATTACHMENT_GROUPINGS = ["type", "attachedTo", "none"] as const;
export type AttachmentGrouping = (typeof ATTACHMENT_GROUPINGS)[number];

export const ATTACHMENT_GROUPING_LABELS: Record<AttachmentGrouping, string> = {
  type: "Type",
  attachedTo: "Attached to",
  none: "No grouping",
};

export const ATTACHMENT_ORDERINGS = ["updated", "name"] as const;
export type AttachmentOrdering = (typeof ATTACHMENT_ORDERINGS)[number];

export const ATTACHMENT_ORDERING_LABELS: Record<AttachmentOrdering, string> = {
  updated: "Last updated",
  name: "Name",
};

export type AttachmentDisplay = {
  grouping: AttachmentGrouping;
  ordering: AttachmentOrdering;
};

export const DEFAULT_ATTACHMENT_DISPLAY: AttachmentDisplay = {
  grouping: "type",
  ordering: "updated",
};

type SourceTask = NonNullable<AttachmentLink["source_task"]>;

export type AttachmentGroup = {
  key: string;
  attachments: ProjectAttachment[];
} & (
  | { by: "type"; kind: LinkKind }
  /** A null task is the project itself. */
  | { by: "attachedTo"; task: SourceTask | null }
  | { by: "none" }
);

/** Attachments in the display's order, split into its groups. With no grouping, one group holds them all. */
export function arrangeAttachments(
  attachments: ProjectAttachment[],
  { grouping, ordering }: AttachmentDisplay,
): AttachmentGroup[] {
  const sorted = sortAttachments(attachments, ordering);
  if (grouping === "none") {
    return sorted.length > 0
      ? [{ key: "all", by: "none", attachments: sorted }]
      : [];
  }
  if (grouping === "type") {
    const byKind = groupBy(sorted, ({ described }) => described.kind);
    return [...byKind]
      .sort(([a], [b]) => compareLinkKinds(a, b))
      .map(([kind, items]) => ({
        key: `type:${kind}`,
        by: "type",
        kind,
        attachments: items,
      }));
  }
  const byTarget = groupBy(sorted, ({ attachedTo }) => attachedTo);
  const onProject = byTarget.get(ON_PROJECT);
  const onTasks = [...byTarget.values()].flatMap((items) => {
    const task = items[0]?.link.source_task;
    return task ? [{ task, items }] : [];
  });
  // Without a sort, tasks run by their latest change, since the items already run newest first.
  if (ordering === "name") {
    onTasks.sort((a, b) => a.task.title.localeCompare(b.task.title));
  }
  return [
    ...(onProject
      ? [
          {
            key: `attachedTo:${ON_PROJECT}`,
            by: "attachedTo" as const,
            task: null,
            attachments: onProject,
          },
        ]
      : []),
    ...onTasks.map(({ task, items }) => ({
      key: `attachedTo:${task.id}`,
      by: "attachedTo" as const,
      task,
      attachments: items,
    })),
  ];
}

function sortAttachments(
  attachments: ProjectAttachment[],
  ordering: AttachmentOrdering,
): ProjectAttachment[] {
  return [...attachments].sort(
    ordering === "name"
      ? (a, b) =>
          a.described.title.localeCompare(b.described.title) ||
          newestFirst(a, b)
      : newestFirst,
  );
}

function groupBy<K, T>(items: T[], keyOf: (item: T) => K): Map<K, T[]> {
  const groups = new Map<K, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return groups;
}

/** Attachments added or changed in the last `days`, newest first, up to `limit`. */
export function recentAttachments(
  attachments: ProjectAttachment[],
  {
    days,
    limit,
    now = new Date(),
  }: { days: number; limit: number; now?: Date },
): ProjectAttachment[] {
  const since = subDays(now, days).getTime();
  return attachments
    .filter(({ touchedAt }) => toTime(touchedAt) >= since)
    .sort(newestFirst)
    .slice(0, limit);
}

// Hasura's timestamps carry an offset and microseconds, so they compare as times, not strings.
function toTime(iso: string): number {
  return new Date(iso).getTime();
}
